<template>
  <poppins-card class="mt-16 py-24 box-shadow-none !bg-transparent sm:box-shadow-sm sm:bg-white sm:px-24">
    <div>
      <div class="flex justify-between">
        <h2 class="text-h2">Pet {{ petIndex + 1 }}</h2>
        <button v-if="petCount > 1" class="remove-pet-btn text-grey-clickable underline" @click="$emit('remove-pet', petIndex)">
          Remove pet
        </button>
      </div>
      <form :id="`pet-details-form-${petIndex}`" class="flex flex-col gap-24 mt-16">
        <label :for="`pet-details-form-${petIndex}`" class="sr-only">Tell us about your pet</label>
        <!-- Pet Type -->
        <div>
          <h3 class="text-strong-body">Type</h3>
          <div class="flex flex-col gap-24 mt-12">
            <radio-button
              v-for="type in petTypeOptions"
              :id="`pet-type-${petIndex}-${type.value}`"
              :key="type.value"
              :radio-name="`petType-${petIndex}`"
              :radio-value="type.value"
              :label-name="type.label"
              :checked="selectedType === type.value || (showOtherPetSearch && type.value === 'OTHER')"
              @update:modelValue="handlePetTypeChange"
            />
          </div>
          <search-combo-box
            v-if="showOtherPetSearch"
            id="otherPetSearch"
            ref="otherPetSearchComboBox"
            name="other-pet-search"
            labelName="Other: i.e. bird, fish, etc"
            :results="filteredPetTypes"
            :value="petTypeSearchInput"
            :helperText="petTypeSearchHelperText"
            class="mt-16"
            type="text"
            @selected="handleOtherPetSelected"
            @valueUpdated="filterPetTypes"
            @blur="handleOtherPetSearchBlur"
          />
        </div>

        <!-- Dog Size -->
        <div v-if="selectedType === 'DOG'" ref="dogSizeSelection">
          <h3 class="text-strong-body">Size</h3>
          <div class="flex flex-col gap-24 mt-12">
            <radio-button
              v-for="size in dogSizeOptions"
              :id="`dog-size-${petIndex}-${size.value}`"
              :key="size.value"
              :radio-name="`dogSize-${petIndex}`"
              :radio-value="size.value"
              :label-name="size.label"
              :checked="petData.size === size.value"
              @update:modelValue="$emit('update-pet', { size: $event })"
            />
          </div>
        </div>

        <!-- Dog Breed -->
        <div v-if="selectedType === 'DOG'" ref="dogBreedSelection">
          <h3 class="text-strong-body">Breed</h3>
          <search-combo-box
            id="dogBreedSearch"
            name="dog-breed-search"
            labelName="Ex. Poodle"
            :results="filteredBreeds"
            :options="[]"
            :value="breedSearchInput"
            :helperText="breedSearchHelperText"
            class="mt-16"
            type="text"
            @selected="handleBreedSelected"
            @valueUpdated="filterBreeds"
            @blur="handleBreedSearchBlur"
          />
        </div>

        <!-- Requires Care -->
        <div v-if="selectedType">
          <h3 class="text-strong-body">Is the sitter expected to care for this pet in any way?</h3>
          <div class="flex flex-col gap-24 mt-12">
            <radio-button
              v-for="option in petRequiresCareOptions"
              :id="`requires-care-${petIndex}-${option.value}`"
              :key="option.label"
              :radio-name="`requiresCare-${petIndex}`"
              :radio-value="option.value"
              :label-name="option.label"
              :checked="petData.requiresCare === (option.value === 'YES')"
              @update:modelValue="handleSitterResponsibilityChange"
            />
          </div>
        </div>

        <!-- Pet Care Responsibilities -->
        <div v-if="petData.requiresCare">
          <h3 class="text-strong-body">Select which care responsibilities would be expected of the sitter.</h3>
          <div>
            <poppins-checkbox
              v-for="care in careResponsibilitiesForType"
              :key="care.value"
              :checkbox-id="`pet-care-responsibilities-${petIndex}-${care.value}`"
              :label-name="care.label"
              :value="care.value"
              :model-value="petData.petCareResponsibilities?.includes(care.value)"
              class="mt-12"
              @update:modelValue="(isChecked) => handleResponsibilitiesChange(isChecked, care.value)"
            />
          </div>
        </div>
      </form>
    </div>
  </poppins-card>
</template>

<script lang="ts">
import PoppinsCard from '@/components/card/Card.vue';
import RadioButton from '@/components/radio_button/RadioButton.vue';
import SearchComboBox from '@/components/search_combo_box/SearchComboBox.vue';
import PoppinsCheckbox from '@/components/checkbox/Checkbox.vue';
import {
  BASE_PET_TYPES,
  OTHER_PET_TYPES,
  DOG_SIZES,
  DOG_CARE_RESPONSIBILITIES,
  CAT_CARE_RESPONSIBILITIES,
  OTHER_PET_CARE_RESPONSIBILITIES,
  PET_REQUIRES_CARE,
  getBreedsForSearchComboBox,
} from '@/helpers/petDetailsHelper';

export default {
  name: 'PetDetailsForm',
  components: { PoppinsCard, RadioButton, SearchComboBox, PoppinsCheckbox },
  props: {
    petData: {
      type: Object,
      required: true,
    },
    petIndex: {
      type: Number,
      required: true,
    },
    petCount: {
      type: Number,
      required: true,
    },
  },
  data() {
    const initialPetType = this.petData.petType ? OTHER_PET_TYPES.find((type) => type.value === this.petData.petType) : null;
    const initialBreed = this.petData.breed
      ? getBreedsForSearchComboBox().find((breed) => breed.value === this.petData.breed)
      : null;

    return {
      petTypeOptions: BASE_PET_TYPES,
      selectedType: this.petData.petType,
      petTypeSearchInput: initialPetType?.display || '',
      petTypeSearchHelperText: '',
      filteredPetTypes: OTHER_PET_TYPES,
      petRequiresCareOptions: PET_REQUIRES_CARE,
      dogSizeOptions: DOG_SIZES,
      breedSearchInput: initialBreed?.display || '',
      breedSearchHelperText: '',
      filteredBreeds: getBreedsForSearchComboBox(),
    };
  },
  computed: {
    careResponsibilitiesForType() {
      const map = {
        DOG: DOG_CARE_RESPONSIBILITIES,
        CAT: CAT_CARE_RESPONSIBILITIES,
        OTHER: OTHER_PET_CARE_RESPONSIBILITIES,
      };

      return map[this.petData.petType] || OTHER_PET_CARE_RESPONSIBILITIES;
    },
    showOtherPetSearch() {
      return this.selectedType !== 'DOG' && this.selectedType !== 'CAT' && this.selectedType;
    },
  },
  methods: {
    handlePetTypeChange(value: string) {
      // Clear any existing form details when pet type changes
      this.resetFormDetails();
      this.selectedType = value;
      this.$emit('update-pet', { petType: value });

      if (value !== 'OTHER') {
        this.petTypeSearchInput = '';
      }
    },
    filterPetTypes(input: string) {
      this.petTypeSearchInput = input;
      const searchRegex = new RegExp(input, 'i');
      this.filteredPetTypes = OTHER_PET_TYPES.filter((type) => searchRegex.test(type.display));
    },
    handleOtherPetSelected(petTypeData: { display: string; value: string }) {
      this.petTypeSearchInput = petTypeData.display;
      this.$emit('update-pet', { petType: petTypeData.value });
    },
    handleOtherPetSearchBlur() {
      this.petTypeSearchHelperText = '';
      if (this.petTypeSearchInput && this.filteredPetTypes.length > 0 && !this.petData.petType) {
        this.petTypeSearchHelperText = 'Please select your pet type from the list';
      }
    },
    handleSitterResponsibilityChange(value: string) {
      // Clear any existing care responsibilities if 'No' is selected
      if (value === 'NO') {
        this.$emit('update-pet', { petCareResponsibilities: [] });
      }
      this.$emit('update-pet', { requiresCare: value === 'YES' });
    },
    filterBreeds(input: string) {
      this.breedSearchInput = input;
      const allBreeds = getBreedsForSearchComboBox();
      const searchRegex = new RegExp(input, 'i');
      this.filteredBreeds = allBreeds.filter((breed) => searchRegex.test(breed.display));
    },
    handleBreedSelected(breedData: { display: string; value: string }) {
      this.breedSearchInput = breedData.display;
      this.$emit('update-pet', { breed: breedData.value });
    },
    handleBreedSearchBlur() {
      this.breedSearchHelperText = '';
      if (this.breedSearchInput && this.filteredBreeds.length > 0 && !this.petData.breed) {
        this.breedSearchHelperText = 'Please select your breed from the list';
      }
    },
    handleResponsibilitiesChange(isChecked: boolean, careValue: string) {
      const current = this.petData.petCareResponsibilities || [];
      const updated = isChecked ? [...current, careValue] : current.filter((value) => value !== careValue);
      this.$emit('update-pet', { petCareResponsibilities: updated });
    },
    resetFormDetails() {
      this.$emit('update-pet', { requiresCare: null, size: null, breed: null, petCareResponsibilities: [] });
    },
  },
};
</script>
